/**
 * Page types for GTM list names.
 * @readonly
 * @enum {string}
 */
const VIEW_ITEM_LIST_NAMES = {
    HOMEPAGE: 'list_homepage',
    REISEFINDER: 'list_reisefinder',
    DESTINATION: 'list_destination',
    CATEGORY: 'list_category',
    SUB_CATEGORY: 'list_subcategory',
}

/**
 * Selectors for the lists on each page type
 * @readonly
 * @enum {string}
 */
const VIEW_ITEM_LIST_SELECTORS = {
    HOMEPAGE: 'div:has(> .item-teaser)',
    REISEFINDER: '.grid__column:has(div div .item-teaser)',
    DESTINATION: '[data-stick-parent] .grid__column:has(div div .item-teaser)',
    CATEGORY: '.panel:has(div > .item-teaser)',
    SUB_CATEGORY: '[data-stick-parent] .grid__column:has(div div .item-teaser)',
}

/**
 * Processes a teaser list, extracts teaser details, and dispatches a GTM event.
 *
 * @param {HTMLElement} teaserList - The container element for the teaser items.
 * @param {IntersectionObserver} observer - The observer instance for unobserving the list.
 * @param {string} listName - The name of the item list being tracked.
 * @param {number} listIndex - The index of the list on the page.
 */
function processTeaserList(teaserList, observer, listName, listIndex) {
    const teasers = Array.from(teaserList.querySelectorAll('.item-teaser'));
    if (teasers.length === 0) {
        console.warn('SKR: No teasers found in the list for tracking');
        return;
    }

    const containerGrid = teaserList.closest('.grid');
    if (!containerGrid) {
        return console.warn('SKR: Could not get the teaser list grid for tracking!');
    }

    const items = teasers.map((teaser, index) => {
        const getTextContent = (selector, fallback = 'n/A') => {
            const el = teaser.querySelector(selector);
            return el ? el.textContent.replace(/\s+/g, ' ').trim() : fallback;
        };

        const criteoProductCode = teaser.getAttribute('data-criteo-product-code');

        const regionClass = Array.from(teaser.classList)
            .find(className => className.startsWith('item-teaser--'));
        const regionName = regionClass ? regionClass.replace('item-teaser--', '').replace(/-/g, ' ') : 'n/A';

        const itemPrice = getTextContent('.item-teaser__info__price');
        const cleanItemPrice = parseInt(itemPrice.replace(/\D/g, '')) || NOT_AVAILABLE_TEXT;

        return {
            item_position: index + 1,
            item_name: getTextContent('.item-teaser__info__subtitle'),
            item_id: criteoProductCode ? criteoProductCode.trim() : 'n/A',
            price: cleanItemPrice,
            item_reiseland: getTextContent('.item-teaser__info__title'),
            item_region: regionName,
        };
    });

    dispatchGtmEvent({
        event: 'view_item_list',
        event_name: 'view_item_list',
        ecommerce: {
            item_list_name: listName,
            item_count: items.length,
            item_position: listIndex,
            items
        }
    });

    observer.unobserve(teaserList);
}

/**
 * Tracks visible teaser lists and processes them when they meet visibility criteria.
 *
 * homepage: /
 * reisefinder page: https://skr-site.ddev.site/reisefinder/
 * destination page: https://skr-site.ddev.site/sueddeutschland-urlaub/bayrischer-wald-urlaub-benediktinerabtei-niederaltaich/
 * category page: https://skr-site.ddev.site/reiseziele/asien-reisen/
 */
function trackVisibleTeaserLists() {
    const breadcrumbLinks = document.querySelectorAll('.breadcrumb__list a');
    const firstBreadcrumb = breadcrumbLinks.length > 0 ? breadcrumbLinks[0].textContent.trim() : '';
    const secondBreadcrumb = breadcrumbLinks.length > 1 ? breadcrumbLinks[1].textContent.trim() : '';
    let listName = 'n/A';
    let listSelector = null;

    if (window.location.pathname === '/') {
        listName = VIEW_ITEM_LIST_NAMES.HOMEPAGE;
        listSelector = VIEW_ITEM_LIST_SELECTORS.HOMEPAGE;
    } else if (firstBreadcrumb === 'Reisefinder' || secondBreadcrumb === 'Reisefinder') {
        listName = VIEW_ITEM_LIST_NAMES.REISEFINDER;
        listSelector = VIEW_ITEM_LIST_SELECTORS.REISEFINDER;
    } else if (firstBreadcrumb && firstBreadcrumb !== 'Reiseziele') {
        listName = VIEW_ITEM_LIST_NAMES.SUB_CATEGORY;
        listSelector = VIEW_ITEM_LIST_SELECTORS.SUB_CATEGORY;
    } else if (firstBreadcrumb === 'Reiseziele' && secondBreadcrumb && secondBreadcrumb !== 'Reisefinder') {
        listName = VIEW_ITEM_LIST_NAMES.DESTINATION;
        listSelector = VIEW_ITEM_LIST_SELECTORS.DESTINATION;
    } else if ((secondBreadcrumb && secondBreadcrumb !== 'Reisefinder') || (firstBreadcrumb && firstBreadcrumb !== 'Reiseziele')) {
        listName = VIEW_ITEM_LIST_NAMES.CATEGORY;
        listSelector = VIEW_ITEM_LIST_SELECTORS.CATEGORY;
    }

    if (!listSelector) {
        console.warn('SKR: No valid listSelector found for the view_item_list gtm event');
        return;
    }

    if (!listName) {
        console.warn('SKR: No valid listName found for the view_item_list gtm event');
        return;
    }

    setTimeout(() => {
        const teaserLists = document.querySelectorAll(listSelector);
        if (teaserLists.length === 0) {
            console.warn('SKR: no teasers found. Could be incorrect selector!');
            return;
        }
        const pixelThreshold = 100;

        const observerOptions = {
            root: null,
            rootMargin: `-${pixelThreshold}px 0px -${pixelThreshold}px 0px`,
            threshold: 0
        };

        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !entry.target.dataset.processed) {
                    entry.target.dataset.processed = 'true';
                    requestAnimationFrame(() => {
                        const listIndex = Array.from(teaserLists).indexOf(entry.target) + 1;
                        processTeaserList(entry.target, observer, listName, listIndex);
                    })
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        teaserLists.forEach((teaserList, index) => observer.observe(teaserList));
    }, 1000);
}

// Initialize tracking when the document is ready.
if (['complete', 'interactive'].includes(document.readyState)) {
    requestAnimationFrame(trackVisibleTeaserLists);
} else {
    document.addEventListener('DOMContentLoaded', trackVisibleTeaserLists, { once: true });
}
;
define("skr/tracking/viewItemList", function(){});

